<template>
  <!-- Deliberately NO `page-break-avoid` on the list as a whole, only on its rows below. The two
       lists sit side by side in one grid row and have very different lengths, and html2pdf drops
       an `avoid` from anything taller than one printed page while honouring it on anything
       shorter. So a long left-hand list flowed across the break while a short right-hand one was
       pushed whole to the next page — the two columns stopped starting at the same height, and
       the right half of the page went blank. Row-level avoids alone keep a row from being sliced
       and, since the heading lives inside the first row, keep the heading with its first item. -->
  <div v-if="visibleItems.length" data-testid="executive-summary-bar-list">
    <div
      v-for="(item, index) in visibleItems"
      :key="item.id"
      class="mb-4 page-break-avoid"
      data-testid="executive-summary-bar-list-row"
    >
      <!-- The heading sits inside the first row rather than beside it, so the two always share a
           page-break unit. A list longer than a printed page loses its own `page-break-avoid` —
           html2pdf drops an avoid on anything taller than one page — and the break then fell
           between the heading and the rows, stranding the heading at the foot of a page. Do not
           lift this back out to a sibling. -->
      <div v-if="index === 0" class="subtitle1 mb-4" data-testid="executive-summary-bar-list-title">
        <!-- No type class of its own: the mock draws heading and count as a single text node in
             Subtitle 1, so the count inherits the heading's size, weight and colour. -->
        {{ subTitle ? `${title} (${subTitle})` : title }}
      </div>
      <div class="body2 text-break">{{ item.label }}</div>
      <div class="d-flex align-center ga-4">
        <div class="executive-summary-bar-list__track">
          <div
            v-if="item.value > 0"
            class="executive-summary-bar-list__bar"
            :class="`executive-summary-bar-list__bar--${variant}`"
            :style="{ width: `${barWidth(item.value)}%` }"
            data-testid="executive-summary-bar-list-bar"
          ></div>
        </div>
        <span
          class="executive-summary-bar-list__value"
          :class="item.value > 0 ? 'body2' : 'subtitle2'"
        >
          {{ formatReportNumber(item.value) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { ReportTilesVariant } from "@/constants/reports";
import { formatReportNumber } from "@/_helpers/reports";
import type { ExecutiveSummaryBarListItem } from "@/_store/reports/adapters";

export default defineComponent({
  props: {
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      default: "",
    },
    items: {
      type: Array as PropType<ExecutiveSummaryBarListItem[]>,
      required: true,
    },
    variant: {
      type: String as PropType<ReportTilesVariant>,
      default: ReportTilesVariant.INDIGO,
    },
    showZeroValues: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const visibleItems = computed(() =>
      props.showZeroValues ? props.items : props.items.filter(({ value }) => value > 0)
    );

    // Scaled against the largest value rather than the total, so the smallest row stays
    // visible instead of collapsing to a sliver. A computed, so the scale follows a
    // date-range change.
    const highestValue = computed(() =>
      visibleItems.value.reduce((max, { value }) => Math.max(max, value), 1)
    );

    /**
     * @param value - One row's value.
     * @returns The row's bar width as a percentage of the track, relative to
     * {@link highestValue} rather than to the sum of all rows.
     */
    const barWidth = (value: number) => (value / highestValue.value) * 100;

    return { visibleItems, barWidth, formatReportNumber };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-bar-list {
  &__track {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__bar {
    height: 4px;
    border-radius: 10px;

    &--indigo {
      background: rgb(var(--v-theme-indigo-medium));
    }

    &--teal {
      background: rgb(var(--v-theme-labels-blue-full));
    }

    &--violet {
      background: rgb(var(--v-theme-labels-purple-full));
    }
  }

  &__value {
    min-width: 40px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}
</style>
